<script>
  import { onMount } from 'svelte';
  import { API_BASE_URL } from './config.js';

  export let setPage = () => {};

  let stats = null;
  let loading = true;
  let error = '';
  let refreshInterval;

  async function loadStats() {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/claude/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('통계를 불러올 수 없습니다.');
      stats = await res.json();
      error = '';
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  function formatCost(usd) {
    if (!usd) return '$0.000000';
    return `$${usd.toFixed(6)}`;
  }

  function formatDuration(ms) {
    if (!ms) return '0ms';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }

  function formatTime(iso) {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
  }

  function endpointLabel(ep) {
    const map = {
      '/analyze-pdf': 'PDF 분석',
      '/match-resume-jd': '이력서 매칭',
      '/analyze-job-posting': '공고 분석',
    };
    return map[ep] || ep;
  }

  onMount(() => {
    loadStats();
    refreshInterval = setInterval(loadStats, 30_000);
    return () => clearInterval(refreshInterval);
  });
</script>

<div class="claude-dashboard">
  <div class="page-header">
    <h1>Claude Bridge 사용량</h1>
    <div class="header-actions">
      <span class="refresh-hint">30초마다 자동 갱신</span>
      <button class="refresh-btn" on:click={loadStats} disabled={loading}>
        {loading ? '갱신 중...' : '↻ 갱신'}
      </button>
    </div>
  </div>

  {#if error}
    <div class="error-banner">{error}</div>
  {/if}

  {#if loading && !stats}
    <div class="loading">데이터를 불러오는 중...</div>
  {:else if stats}
    <!-- 요약 카드 -->
    <div class="summary-grid">
      <div class="stat-card">
        <div class="stat-label">총 호출 수</div>
        <div class="stat-value">{stats.summary.total_calls.toLocaleString()}</div>
      </div>
      <div class="stat-card success">
        <div class="stat-label">성공</div>
        <div class="stat-value">{stats.summary.success_calls.toLocaleString()}</div>
      </div>
      <div class="stat-card fail">
        <div class="stat-label">실패</div>
        <div class="stat-value">{stats.summary.fail_calls.toLocaleString()}</div>
      </div>
      <div class="stat-card cost">
        <div class="stat-label">총 비용 (USD)</div>
        <div class="stat-value">{formatCost(stats.summary.total_cost_usd)}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">입력 토큰</div>
        <div class="stat-value">{stats.summary.total_input_tokens.toLocaleString()}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">출력 토큰</div>
        <div class="stat-value">{stats.summary.total_output_tokens.toLocaleString()}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">평균 응답 시간</div>
        <div class="stat-value">{formatDuration(stats.summary.avg_duration_ms)}</div>
      </div>
    </div>

    <!-- 엔드포인트별 통계 -->
    {#if Object.keys(stats.by_endpoint).length > 0}
      <div class="section">
        <h2>엔드포인트별 사용량</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th>엔드포인트</th>
              <th>호출 수</th>
              <th>성공</th>
              <th>실패</th>
              <th>비용 (USD)</th>
            </tr>
          </thead>
          <tbody>
            {#each Object.entries(stats.by_endpoint) as [ep, d]}
              <tr>
                <td><span class="endpoint-tag">{endpointLabel(ep)}</span><span class="endpoint-path">{ep}</span></td>
                <td>{d.count}</td>
                <td class="success-text">{d.success}</td>
                <td class="fail-text">{d.count - d.success}</td>
                <td>{formatCost(d.cost)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}

    <!-- 최근 호출 로그 -->
    {#if stats.recent && stats.recent.length > 0}
      <div class="section">
        <h2>최근 호출 기록 (최대 50건)</h2>
        <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th>시각</th>
                <th>엔드포인트</th>
                <th>상태</th>
                <th>응답 시간</th>
                <th>입력 토큰</th>
                <th>출력 토큰</th>
                <th>비용 (USD)</th>
              </tr>
            </thead>
            <tbody>
              {#each stats.recent as log}
                <tr>
                  <td class="time-cell">{formatTime(log.timestamp)}</td>
                  <td><span class="endpoint-tag small">{endpointLabel(log.endpoint)}</span></td>
                  <td>
                    {#if log.success}
                      <span class="badge success">성공</span>
                    {:else}
                      <span class="badge fail">실패</span>
                    {/if}
                  </td>
                  <td>{formatDuration(log.duration_ms)}</td>
                  <td>{log.input_tokens.toLocaleString()}</td>
                  <td>{log.output_tokens.toLocaleString()}</td>
                  <td>{formatCost(log.total_cost_usd)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    {:else}
      <div class="empty-state">아직 Claude Bridge 호출 기록이 없습니다.</div>
    {/if}
  {/if}
</div>

<style>
  .claude-dashboard {
    padding: 24px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }

  .page-header h1 {
    font-size: 1.6rem;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .refresh-hint {
    font-size: 0.82rem;
    color: #888;
  }

  .refresh-btn {
    padding: 7px 16px;
    background: #3273dc;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.88rem;
    font-weight: 500;
  }

  .refresh-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .error-banner {
    background: #fff0f0;
    border: 1px solid #ffc0c0;
    border-radius: 8px;
    padding: 12px 16px;
    color: #c0392b;
    margin-bottom: 20px;
    font-size: 0.9rem;
  }

  .loading {
    text-align: center;
    padding: 60px;
    color: #888;
    font-size: 1rem;
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 16px;
    margin-bottom: 32px;
  }

  .stat-card {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    padding: 16px;
    text-align: center;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  }

  .stat-card.success { border-top: 3px solid #27ae60; }
  .stat-card.fail    { border-top: 3px solid #e74c3c; }
  .stat-card.cost    { border-top: 3px solid #f39c12; }

  .stat-label {
    font-size: 0.78rem;
    color: #888;
    margin-bottom: 8px;
    font-weight: 500;
  }

  .stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a1a2e;
  }

  .section {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 24px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  }

  .section h2 {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    margin: 0 0 16px 0;
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }

  .data-table th {
    background: #f8f9fa;
    padding: 10px 12px;
    text-align: left;
    font-weight: 600;
    color: #555;
    border-bottom: 2px solid #e9ecef;
  }

  .data-table td {
    padding: 10px 12px;
    border-bottom: 1px solid #f0f0f0;
    color: #333;
    vertical-align: middle;
  }

  .data-table tr:last-child td { border-bottom: none; }
  .data-table tr:hover td { background: #fafafa; }

  .table-scroll {
    overflow-x: auto;
  }

  .endpoint-tag {
    display: inline-block;
    background: #e8f0fe;
    color: #3273dc;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.78rem;
    font-weight: 600;
    margin-right: 6px;
  }

  .endpoint-tag.small { font-size: 0.72rem; }

  .endpoint-path {
    font-size: 0.78rem;
    color: #888;
  }

  .badge {
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .badge.success { background: #d4edda; color: #155724; }
  .badge.fail    { background: #f8d7da; color: #721c24; }

  .success-text { color: #27ae60; font-weight: 600; }
  .fail-text    { color: #e74c3c; font-weight: 600; }

  .time-cell {
    font-size: 0.78rem;
    color: #666;
    white-space: nowrap;
  }

  .empty-state {
    text-align: center;
    padding: 40px;
    color: #aaa;
    font-size: 0.95rem;
  }
</style>
